// 🔗 INTEGRAÇÃO RAG COM ANÁLISE IA
// Sistema: HEPATO TX RONDÔNIA

import { DadosAnonimizados, DocumentoRagRecuperado } from "../types";
import {
  buscarContextoGuidelines,
  buscarContextoMultiplo,
  formatarContextoParaPrompt,
  gerarQueriesAutomaticas,
  extrairTopicosParaReRetrieval,
  mapearDocumentosRecuperados,
} from "./services/buscarContexto";
import { RetrievedContext } from "./types";


// ═══════════════════════════════════════════════════════════════════════════
// 🫀 B351 passo 5b (AI_153) — RAG DA MODALIDADE DOADOR VIVO
// ═══════════════════════════════════════════════════════════════════════════
// Por que um caminho PRÓPRIO, e não um ramo dentro do fluxo normal:
//
// 1. (Histórico) `determinarCategorias` não conhecia doador: um candidato
//    saudável caía no fallback ["cirrose", "geral"] e o filtro `$in` de categoria
//    EXCLUÍA o consenso de doador vivo. O filtro de categoria do fluxo normal foi
//    removido no B358 (AI_156); o doador segue com filtro próprio (transplante+ILTS).
//    Medido no índice real: hoje é IMPOSSÍVEL o doador receber uma linha da
//    diretriz certa. Qualquer critério que o texto citasse viria de memória, e
//    seria barrado pela regra de ancoragem de citação (B219/B224).
// 2. `gerarQueriesAutomaticas` anexa a query PT de SITUAÇÃO ESPECIAL a todo caso
//    não-pós-Tx (B326) — e um doador nunca é pós-Tx. O corpus do doador viria
//    com a norma de exceção de pontuação garantida, contradizendo por baixo a
//    proibição de vocabulário do receptor.
//
// Passar por essas funções e depois "consertar" seria somar exceção a regra
// alheia. O caminho próprio é mais curto e não pode ser quebrado por uma regra
// futura de pré-Tx.
//
// As 6 queries foram MEDIDAS contra o índice real (relatório na seção 11-B do
// plano). Uma 7ª, sobre compatibilidade sanguínea e GRWR, foi descartada: o
// melhor score dela foi 0,447, abaixo do corte de 0,55 — não devolvia nada. Em
// seu lugar entrou a de comorbidade cardiometabólica, a melhor de todas as
// testadas, que cobre o eixo de alertas.
//
// ⚠️ MÁXIMO 7 desde o B357 (AI_156): o `ragClient` corta em `queries.slice(0, 7)`
// e o backend recusa mais que isso.
export const QUERIES_DOADOR_VIVO = [
  // Eixo (a) — seleção e elegibilidade [12 trechos, topo 0,686]
  "living liver donor selection eligibility contraindications age BMI",
  // Eixo (b) — remanescente e segurança da hepatectomia [9, 0,669]
  "future liver remnant RLV/TLV 30% right hepatectomy donor safety",
  // Eixo (a) — esteatose e método de medida [12, 0,602]
  "hepatic steatosis MASLD liver biopsy MRI-PDFF living donor graft",
  // Eixo (b) — anatomia vascular e biliar [2, 0,581]
  "anatomical considerations variant biliary anatomy hepatic vein living donor",
  // Eixo (d) — psicossocial, voluntariedade e consentimento [3, 0,620]
  "psychosocial workup voluntariness informed consent donor advocate",
  // Eixo (a) — comorbidade cardiometabólica e rastreio [12, 0,716]
  "living liver donor cardiovascular disease diabetes hypertension malignancy screening contraindication",
];

/**
 * Contexto de diretrizes da modalidade DOADOR VIVO.
 *
 * Filtro por categoria E organização: só a categoria não protege — medido no
 * índice real, com `categories:["transplante"]` sozinho o topo da query de
 * seleção continua sendo a diretriz de transplante do receptor (0,709 contra
 * 0,686) e o da esteatose continua sendo a de doença gordurosa (0,620 contra
 * 0,602). O custo assumido é deixar essas de fora; o que elas trariam é
 * vocabulário de receptor, que é justamente o que se quer manter longe.
 * ⟳ REVISAR se, na validação ao vivo, faltar fundamento para a esteatose.
 *
 * Serve tanto à análise GERAL quanto à de IMAGEM do doador — o mesmo conserto.
 */
export async function obterContextoGuidelinesDoador(): Promise<{
  texto: string;
  documentos: DocumentoRagRecuperado[];
}> {
  try {
    console.log("📚 [B351] Buscando diretrizes de DOADOR VIVO...");

    const context = await buscarContextoMultiplo(QUERIES_DOADOR_VIVO, {
      categories: ["transplante"],
      organizations: ["ILTS"],
      maxChunksTotal: 8,
      distribuirPorTema: false, // B358: caminho calibrado (B351), sem rodízio
    });

    const texto = formatarContextoParaPrompt(context);
    const documentos = mapearDocumentosRecuperados(context.chunks);
    console.log(`   ✅ [B351] ${context.totalResults} trecho(s) de doador vivo`);
    return { texto, documentos };
  } catch (error) {
    // Não-bloqueante: sem contexto, a análise sai sem citar diretriz — o que é
    // preferível a sair com o corpus errado.
    console.error("❌ [B351] Erro ao buscar diretrizes do doador:", error);
    return { texto: "", documentos: [] };
  }
}

/**
 * 📚 B229 fase 2 / B328 (AI_148) — versão que devolve, além do TEXTO do prompt
 * (idêntico ao de sempre), a LISTA de documentos (diretrizes) recuperados —
 * antes descartada após o prompt. Usada pela análise geral (useIAAnalise) para
 * registrar a proveniência. Aditiva: NÃO altera o texto enviado ao modelo.
 */
export async function obterContextoGuidelinesComDocs(
  dados: DadosAnonimizados
): Promise<{ texto: string; documentos: DocumentoRagRecuperado[] }> {
  try {
    console.log("📚 Buscando contexto nos guidelines...");

    // B358 (AI_156): SEM filtro de categoria. A etiqueta `category` gravada no
    // Pinecone é errada em muitos docs (EASL HBV, EASL descompensada, AASLD ascite
    // e a Portaria = "transplante"; Baveno = "complicacoes-vasculares"), e o filtro
    // duro as EXCLUÍA (hepatite B → 1 trecho, da diretriz de hepatite Delta). A
    // seleção agora é por relevância (denso + BM25 + rerank) com teto por documento.

    const queries = gerarQueriesAutomaticas({
      tipoAtendimento: dados.tipoAtendimento,
      transplante: dados.transplante,
      examesLab: dados.examesLab,
      consultas: dados.consultas,
      diagnosticoBase: dados.diagnosticoBase,
      achadosImagem: dados.achadosImagem,
      inscricaoTx: dados.inscricaoTx,
      dadosTransplante: dados.dadosTransplante,
      avaliacaoClinica: (dados as any).avaliacaoClinica,
    });
    console.log(`   Queries geradas: ${queries.length}`);

    if (queries.length === 0) {
      const queryPadrao = gerarQueryPadrao(dados);
      queries.push(queryPadrao);
    }

    const context = await buscarContextoMultiplo(queries, {
      maxChunksTotal: 8,
    });

    const textoContexto = formatarContextoParaPrompt(context);
    const documentos = mapearDocumentosRecuperados(context.chunks);

    console.log(`   ✅ ${context.totalResults} trechos relevantes encontrados`);

    return { texto: textoContexto, documentos };
  } catch (error) {
    console.error("❌ Erro ao buscar contexto dos guidelines:", error);
    return { texto: "", documentos: [] };
  }
}

// Wrapper fino: preserva a assinatura string p/ os demais consumidores (chat,
// análise contextual, laudo especial, imagem) — nenhum deles é tocado.
export async function obterContextoGuidelines(
  dados: DadosAnonimizados
): Promise<string> {
  return (await obterContextoGuidelinesComDocs(dados)).texto;
}

/**
 * 📄 B250 (AI_113) — Contexto RAG do LAUDO ESPECIAL.
 * Soma a busca padrão (pelos DADOS do paciente, INALTERADA) a uma busca extra
 * guiada pela SOLICITAÇÃO em texto livre do médico. Assim o RAG recupera o que
 * ele explicitamente pede (ex.: "atezolizumabe + bevacizumabe / IMbrave150") e
 * as citações podem ser ANCORADAS (B224) — em vez de barradas por falta de
 * fonte. A busca extra NÃO restringe por categoria (recall amplo p/ o termo
 * pedido, como o re-retrieval). Falha na busca extra → cai no contexto base.
 * Isolada: NÃO altera obterContextoGuidelines (chat/análise geral intactos).
 */
export async function obterContextoLaudoEspecial(
  dados: DadosAnonimizados,
  solicitacao: string
): Promise<string> {
  const base = await obterContextoGuidelines(dados);

  const termo = (solicitacao || "").trim();
  if (termo.length < 4) return base;

  try {
    console.log("📚 [laudo-especial] Busca extra guiada pela solicitação...");
    const context = await buscarContextoMultiplo([termo], {
      maxChunksTotal: 4,
    });
    if (context.totalResults === 0) return base;
    // Renumera as fontes do bloco extra com prefixo "S" (solicitação) para NÃO
    // colidir com a numeração "Fonte N" do bloco base concatenado — senão o
    // prompt teria duas "Fonte 1", duas "Fonte 2"… e a ancoragem B224 ("consta
    // de uma Fonte N") ficaria ambígua. Cada fonte fica única: Fonte S1, S2…
    const extra = formatarContextoParaPrompt(context).replace(
      /--- Fonte (\d+):/g,
      "--- Fonte S$1:"
    );
    console.log(
      `   ✅ [laudo-especial] ${context.totalResults} trecho(s) p/ a solicitação`
    );
    return `${base}\n\n═══ FONTES PARA A SOLICITAÇÃO DO MÉDICO ═══\n${extra}\n═══ FIM DAS FONTES DA SOLICITAÇÃO ═══`;
  } catch (error) {
    console.warn(
      "⚠️ [laudo-especial] busca extra falhou, seguindo só com o contexto base:",
      error
    );
    return base;
  }
}

function gerarQueryPadrao(dados: DadosAnonimizados): string {
  if (dados.tipoAtendimento === "pos-transplante") {
    return "liver transplant management complications monitoring";
  }

  if (dados.tipoAtendimento === "pre-transplante") {
    return "cirrhosis liver transplant evaluation indication";
  }

  return "chronic liver disease management guidelines";
}

// Re-exportar funções úteis
export {
  buscarContextoGuidelines,
  formatarContextoParaPrompt,
} from "./services/buscarContexto";

/**
 * 🔄 RE-RETRIEVAL CONDICIONAL — Etapa 2
 * Busca guidelines complementares baseados nos tópicos da análise da Etapa 1
 */
export async function reRetrievalCondicional(
  analiseEtapa1: string,
  queriesOriginais: string[]
): Promise<string> {
  try {
    const queriesNovas = extrairTopicosParaReRetrieval(analiseEtapa1);

    // Filtrar queries que já foram usadas na Etapa 1
    const originaisLower = queriesOriginais.map((q) => q.toLowerCase());
    const queriesFiltradas = queriesNovas.filter(
      (q) => !originaisLower.some((orig) => orig === q.toLowerCase())
    );

    if (queriesFiltradas.length === 0) {
      console.log("🔄 Re-retrieval: nenhum tópico novo identificado");
      return "";
    }

    console.log(
      `🔄 Re-retrieval: ${
        queriesFiltradas.length
      } tópico(s) novo(s) → ${queriesFiltradas
        .map((q) => q.substring(0, 40))
        .join(" | ")}`
    );

    const context = await buscarContextoMultiplo(queriesFiltradas, {
      maxChunksTotal: 4,
    });

    if (context.totalResults === 0) {
      console.log("🔄 Re-retrieval: nenhum chunk complementar encontrado");
      return "";
    }

    const texto = formatarContextoParaPrompt(context);
    console.log(
      `🔄 Re-retrieval: ${context.totalResults} chunks complementares obtidos`
    );
    return `\n═══ GUIDELINES COMPLEMENTARES (Re-retrieval Etapa 2) ═══\n${texto}\n═══ FIM DOS GUIDELINES COMPLEMENTARES ═══\n`;
  } catch (error) {
    console.warn("⚠️ Re-retrieval falhou, continuando sem:", error);
    return "";
  }
}
